/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import DataTable, { SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@stoneage/App/Components/Notify";
import getMyTrainingCourses from "@stoneage/App/Handlers/MyTrainingCourses/GetMyTrainingCourses";
import { CoursesModel } from "@stoneage/App/Services/MyTrainingCourses/MyTrainingCoursesService";
import Pagination from "@stoneage/MobiusOverrides/Pagination/Pagination";
import { parseDate } from "@stoneage/XDL/Utilities/helpers";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    getMyTrainingCourses,
};

type Props = ResolveThunks<typeof mapDispatchToProps>;

export interface MyTrainingCoursesStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const myTrainingCoursesStyles: MyTrainingCoursesStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const styles = myTrainingCoursesStyles;

const MyTrainingCourses = ({ getMyTrainingCourses }: Props) => {
    const [isLoading, setIsloading] = useState(false);
    const [courses, setCourses] = useState<CoursesModel[]>([]);
    const [updateCourses, setUpdateCourses] = useState<boolean>(false);
    const [paginationData, setPaginationData] = useState<PaginationModel | null>(null);
    const [sortData, setSortData] = useState({
        page: 1,
        pageSize: 8,
        sortBy: "courseNumber",
    });

    useEffect(() => {
        setIsloading(true);
        getMyTrainingCourses({
            onError: error => {
                setIsloading(false);
            },
            onSuccess(resultProps) {
                if (resultProps?.successful) {
                    resultProps.result.myTrainingCourses && setCourses(resultProps.result.myTrainingCourses);
                    setPaginationData(resultProps.result.pagination);
                } else if (resultProps?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.errorMessage);
                }
                setIsloading(false);
            },
        });
    }, [updateCourses]);

    const getCoursesQuery = (newSortData: { page: number; pageSize: number; sortBy: string }) => {
        setIsloading(true);
        getMyTrainingCourses({
            ...newSortData,
            onError: error => {
                setIsloading(false);
            },
            onSuccess(resultProps) {
                if (resultProps?.successful) {
                    resultProps.result.myTrainingCourses && setCourses(resultProps.result.myTrainingCourses);
                    setPaginationData(resultProps.result.pagination);
                } else if (resultProps?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.errorMessage);
                }
                setIsloading(false);
            },
        });
        setSortData(newSortData);
    };

    const changePage = (newPageIndex: number) => {
        const newSortData = {
            ...sortData,
            page: newPageIndex,
        };
        getCoursesQuery(newSortData);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        const newSortData = {
            ...sortData,
            pageSize: newPageSize,
        };
        getCoursesQuery(newSortData);
    };

    const headerClick = (sortField: string) => {
        const direction = sortData.sortBy === sortField ? " DESC" : "";
        const sortBy = sortField + direction;
        const newSortData = {
            ...sortData,
            sortBy,
        };
        getCoursesQuery(newSortData);
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (sortData.sortBy === sortField) {
            sorted = "ascending";
        } else if (sortData.sortBy === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!courses.length) {
        return <Notify messageType="info" body={translate("No Courses Found")} />;
    }

    return (
        <>
            <DataTable className="mobile-view">
                <DataTableHead>
                    <DataTableHeader sorted={sorted("createdOn")} onSortClick={() => headerClick("createdOn")}>
                        {translate("Reg. Date")}
                    </DataTableHeader>
                    <DataTableHeader sorted={sorted("CourseName")} onSortClick={() => headerClick("CourseName")}>
                        {translate("Course Name")}
                    </DataTableHeader>
                    <DataTableHeader
                        sorted={sorted("thinkificExpiryDate")}
                        onSortClick={() => headerClick("thinkificExpiryDate")}
                    >
                        {translate("Expiration Date")}
                    </DataTableHeader>
                    <DataTableHeader
                        sorted={sorted("completionDate")}
                        onSortClick={() => headerClick("completionDate")}
                    >
                        {translate("Completion Date")}
                    </DataTableHeader>
                    <DataTableHeader
                        sorted={sorted("thinkificStatus")}
                        onSortClick={() => headerClick("thinkificStatus")}
                    >
                        {translate("Status")}
                    </DataTableHeader>
                    {/* <DataTableHeader>{translate("Synced With Thinkific")}</DataTableHeader> */}
                </DataTableHead>
                <DataTableBody>
                    {courses.map(
                        ({
                            registrationDate,
                            courseName,
                            syncedWithThinkific,
                            expirationDate,
                            completionDate,
                            status,
                        }) => {
                            return (
                                <DataTableRow key={courseName}>
                                    <DataTableCell data-th={translate("Reg. Date")}>
                                        {parseDate(registrationDate)}
                                    </DataTableCell>
                                    <DataTableCell data-th={translate("Course Name")}>{courseName}</DataTableCell>
                                    <DataTableCell data-th={translate("Expiration Date")}>
                                        {parseDate(expirationDate)}
                                    </DataTableCell>
                                    <DataTableCell data-th={translate("Completion Date")}>
                                        {parseDate(completionDate)}
                                    </DataTableCell>
                                    <DataTableCell data-th={translate("Status")}>{status}</DataTableCell>
                                    {/* <DataTableCell data-th={translate("Synced With Thinkific")}> {syncedWithThinkific}</DataTableCell> */}
                                </DataTableRow>
                            );
                        },
                    )}
                </DataTableBody>
            </DataTable>
            {paginationData && paginationData?.numberOfPages > 1 && (
                <Pagination
                    resultsCount={paginationData.totalItemCount}
                    currentPage={sortData.page}
                    resultsPerPage={sortData.pageSize}
                    resultsPerPageOptions={paginationData?.pageSizeOptions}
                    onChangePage={changePage}
                    onChangeResultsPerPage={changeResultsPerPage}
                    isBottom={true}
                    pageHasMultiplePaginations={true}
                />
            )}
        </>
    );
};

export const definition: WidgetDefinition = {
    displayName: "My Training Course List",
    group: "My Training Courses" as any,
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(MyTrainingCourses),
    definition,
};

export default widgetModule;
